import {
  createDarkTheme,
  createLightTheme,
  type BrandVariants,
  type Theme,
} from '@fluentui/react-components';

/**
 * PTC brand ramp — the fleet design standard (ptc-core / ptc-supervision /
 * ptc-statetax). 16-stop ramp anchored at #0078d4 (slot 80 — Fluent's primary
 * brand background), matched to the statetax design language (primary #0078d4,
 * dark #0066b8, nav tint #e6f1fa).
 */
export const ptcBrand: BrandVariants = {
  10: '#00253f',
  20: '#003053',
  30: '#003b67',
  40: '#00467c',
  50: '#005090',
  60: '#005ba4',
  70: '#0066b8',
  80: '#0078d4',
  90: '#1d87d9',
  100: '#3a96de',
  110: '#56a5e2',
  120: '#73b5e7',
  130: '#90c4ec',
  140: '#add3f1',
  150: '#c9e2f5',
  160: '#e6f1fa',
};

export type ThemeMode = 'light' | 'dark';

/** Fleet corner-radius spec (ptc-core / statetax card & shell language). */
const ptcShape = {
  borderRadiusSmall: '4px',
  borderRadiusMedium: '4px',
  borderRadiusLarge: '8px',
  borderRadiusXLarge: '12px',
} as const;

/**
 * Light theme is intentionally close to Fluent's `webLightTheme` (what the app
 * shipped with) so the existing light UI stays pixel-faithful; only the brand
 * ramp + corner radii are pinned to the PTC standard.
 */
export const ptcLightTheme: Theme = {
  ...createLightTheme(ptcBrand),
  ...ptcShape,
};

/**
 * Calibrated dark surface stack — a cool, PTC-blue-tinted neutral ramp
 * (hue ~212 deg, low saturation) instead of Fluent's flat grays. Never pure
 * black. Elevation reads page < card < popover:
 *
 *   #0d1013  deepest (background5)
 *   #101418  app body (background4)
 *   #14181e  page canvas (background3, --tl-bg-page)
 *   #171c22  inset wells / rows on cards (background2)
 *   #1d232b  cards, inputs (background1, --tl-bg-card)
 *   #262e38  popovers / menus / dialogs (--tl-bg-popover, via index.css)
 *
 * Hover states step *up* (lighter = closer to the light source); pressed
 * states step down. Values shared verbatim with ptc-supervision's dark theme.
 */
export const ptcDarkTheme: Theme = {
  ...createDarkTheme(ptcBrand),
  ...ptcShape,

  // ---- Surfaces ----
  colorNeutralBackground1: '#1d232b',
  colorNeutralBackground1Hover: '#242b34',
  colorNeutralBackground1Pressed: '#1a2027',
  colorNeutralBackground1Selected: '#232a33',
  colorNeutralBackground2: '#171c22',
  colorNeutralBackground2Hover: '#1d232b',
  colorNeutralBackground2Pressed: '#14181e',
  colorNeutralBackground2Selected: '#1d232b',
  colorNeutralBackground3: '#14181e',
  colorNeutralBackground3Hover: '#191f26',
  colorNeutralBackground3Pressed: '#101418',
  colorNeutralBackground3Selected: '#191f26',
  colorNeutralBackground4: '#101418',
  colorNeutralBackground5: '#0d1013',
  colorNeutralBackground6: '#232a33',
  colorNeutralBackgroundDisabled: '#171c22',
  colorSubtleBackgroundHover: '#262e38',
  colorSubtleBackgroundPressed: '#202730',
  colorSubtleBackgroundSelected: '#242c35',

  // ---- Foregrounds (soft off-whites — no glaring #fff body text) ----
  colorNeutralForeground1: '#eef2f6',
  colorNeutralForeground2: '#ccd4dc',
  colorNeutralForeground3: '#99a3b0',
  colorNeutralForeground4: '#8b95a2',
  colorNeutralForegroundDisabled: '#5b6573',

  // ---- Strokes (tinted to match the surface ramp) ----
  colorNeutralStroke1: '#454e5a',
  colorNeutralStroke1Hover: '#525c69',
  colorNeutralStroke1Pressed: '#454e5a',
  colorNeutralStroke1Selected: '#525c69',
  colorNeutralStroke2: '#333b45',
  colorNeutralStroke3: '#262e38',
  colorNeutralStrokeSubtle: '#333b45',
  colorNeutralStrokeDisabled: '#2b323c',
  colorNeutralStrokeAccessible: '#a5aeba',
  colorNeutralStrokeAccessibleHover: '#bcc4cf',
  colorNeutralStrokeAccessiblePressed: '#a5aeba',
  // Cards/popovers draw a 1px "transparent" stroke — in dark, a faint light
  // hairline substitutes for shadows as the elevation cue.
  colorTransparentStroke: 'rgba(255, 255, 255, 0.08)',
};

export function themeForMode(mode: ThemeMode): Theme {
  return mode === 'dark' ? ptcDarkTheme : ptcLightTheme;
}
